// needs-decision-auto-decision-evaluation.ts — V2-1 (spec V2 §4.1).
//
// Ghép 5 điều kiện auto cho MỘT result `needs_decision`, đọc lại mọi nguồn ở
// đúng thời điểm xét (không cache): thẩm quyền G0 (decision-policy-authority-
// reader.ts), token ranh giới, số auto đã ghi trong journal, directive chờ.
// `skipReason` CHỈ có khi policy hiệu lực = `auto` (E4) — policy `ask` thì câu
// hỏi y như V2-0, không dòng "không auto vì", không `decision_auto_skipped`.
// Không có `runConfig` (unit test / đường chưa nối) ⇒ không xét gì: `ask`.
import { evaluateDecisionAutoRules } from "./decision-auto-apply-rules.js";
import {
  collectDecisionBoundaryTokens,
  type DecisionAutoRunConfig,
} from "./decision-auto-boundary-tokens.js";
import { readDecisionPolicyAuthority } from "./decision-policy-authority-reader.js";
import { countJournaledAutoDecisions } from "./decision-policy-journal-events.js";
import { isDirectiveProcessed, listInboxDirectiveIds, readDirective } from "./directive-inbox.js";
import {
  type OptionIdLabelPair,
  readNeedsDecisionOptions,
} from "./needs-decision-option-id-label-pairs.js";

export interface NeedsDecisionAutoContext {
  controlRoot: string;
  repoDir: string;
  ticketKind: string;
  runConfig: DecisionAutoRunConfig | undefined;
}

export type NeedsDecisionAutoOutcome =
  | { mode: "ask"; skipReason?: string }
  | {
      mode: "auto";
      option: OptionIdLabelPair;
      claims: Record<string, boolean>;
      g0RequestId: string;
      blueprintHash: string;
    };

/** §4.1 điều 5. Directive không đọc được ⇒ coi như đang chờ (fail-closed). */
export async function hasPendingPauseOrRedirectDirective(controlRoot: string): Promise<boolean> {
  for (const id of await listInboxDirectiveIds(controlRoot)) {
    if (await isDirectiveProcessed(controlRoot, id)) continue;
    try {
      const directive = await readDirective(controlRoot, id);
      if (directive === undefined) continue;
      if (directive.kind === "pause" || directive.kind === "redirect") return true;
    } catch {
      return true;
    }
  }
  return false;
}

export async function evaluateNeedsDecisionAuto(
  ctx: NeedsDecisionAutoContext,
  result: Record<string, unknown>,
): Promise<NeedsDecisionAutoOutcome> {
  if (ctx.runConfig === undefined) return { mode: "ask" };
  const authority = await readDecisionPolicyAuthority(ctx.controlRoot, ctx.repoDir);
  if (authority.policy !== "auto") return { mode: "ask" };

  try {
    const options = readNeedsDecisionOptions(result.options);
    const tokens = await collectDecisionBoundaryTokens(ctx.controlRoot, ctx.repoDir, ctx.runConfig);
    if (!tokens.ok) return { mode: "ask", skipReason: tokens.problem };
    const verdict = evaluateDecisionAutoRules({
      pairs: options.pairs,
      pairsProblem: options.pairsProblem,
      recommendedOptionId: result.recommended_option_id,
      boundaryClaims: result.boundary_claims,
      summary: typeof result.summary === "string" ? result.summary : "",
      ticketKind: ctx.ticketKind,
      tokens: tokens.tokens,
      autoDecisionsSoFar: await countJournaledAutoDecisions(ctx.controlRoot),
      maxAutoDecisions: ctx.runConfig.maxAutoDecisionsPerMission,
      pendingPauseOrRedirect: await hasPendingPauseOrRedirectDirective(ctx.controlRoot),
    });
    if (!verdict.apply) return { mode: "ask", skipReason: verdict.reason };
    return {
      mode: "auto",
      option: verdict.option,
      claims: verdict.claims,
      g0RequestId: authority.g0RequestId,
      blueprintHash: authority.blueprintHash,
    };
  } catch (err) {
    return { mode: "ask", skipReason: `lỗi khi xét auto (${(err as Error).message})` };
  }
}
